import { create } from 'zustand';
import { supabase } from '@/lib/supabase';
import { processCombosAndDrops } from '@/lib/gamificationEngine';
import { trackEvent } from '@/lib/analytics';
import type { GameState } from '@/types/database';

/**
 * Gamification store — XP, Gems, Streak, Level state.
 * Reads from game_state table. Updates optimistically.
 */

interface GameStore {
  gameState: GameState | null;
  loading: boolean;

  // Loaders
  fetchGameState: (userId: string) => Promise<void>;

  // Actions
  awardXP: (userId: string, action: string, amount: number, room?: string) => Promise<void>;
  spendGems: (userId: string, amount: number, reason: string) => Promise<boolean>;
  refreshStreak: (userId: string) => Promise<void>;

  // Internal
  setGameState: (gs: GameState) => void;
}

export const useGameStore = create<GameStore>((set, get) => ({
  gameState: null,
  loading: false,

  fetchGameState: async (userId: string) => {
    set({ loading: true });
    try {
      const { data, error } = await supabase
        .from('game_state')
        .select('*')
        .eq('user_id', userId)
        .single();

      if (error && error.code === 'PGRST116') {
        // No game state yet — create one
        const { data: newState } = await supabase
          .from('game_state')
          .insert({
            user_id: userId,
            total_xp: 0,
            current_level: 1,
            level_name: 'Novice',
            gems_balance: 0,
            current_streak: 0,
            longest_streak: 0,
            streak_type: 'fire',
            streak_freeze_available: false,
            total_badges: 0,
            combo_multiplier: 1.0,
            combo_count: 0,
            last_active_date: null,
            combo_last_action_at: null,
          })
          .select()
          .single();

        if (newState) set({ gameState: newState, loading: false });
        return;
      }

      if (data) set({ gameState: data as GameState, loading: false });
    } catch (err) {
      console.error('Failed to fetch game state:', err);
      set({ loading: false });
    }
  },

  awardXP: async (userId, action, amount, room) => {
    const gs = get().gameState;
    if (!gs) return;

    const prevLevel = gs.current_level;

    // Optimistic update
    const newXP = gs.total_xp + amount;
    set({
      gameState: { ...gs, total_xp: newXP },
    });

    // Insert XP event (triggers level-up function in DB)
    await supabase.from('xp_events').insert({
      user_id: userId,
      action,
      xp_amount: amount,
      room: room ?? null,
      multiplier: gs.combo_multiplier,
      source: null,
    });

    await processCombosAndDrops(userId, action, room ?? null);

    // Re-fetch to get updated level
    await get().fetchGameState(userId);
    const next = get().gameState;
    if (next && next.current_level > prevLevel) {
      trackEvent('level_up', { level: next.current_level });
    }
  },

  spendGems: async (userId, amount, reason) => {
    const gs = get().gameState;
    if (!gs || gs.gems_balance < amount) return false;

    // Optimistic
    set({
      gameState: { ...gs, gems_balance: gs.gems_balance - amount },
    });

    await supabase.from('gem_transactions').insert({
      user_id: userId,
      amount: -amount,
      reason,
      balance_after: gs.gems_balance - amount,
    });

    return true;
  },

  refreshStreak: async (userId) => {
    // Re-fetch game state to get server-calculated streak
    await get().fetchGameState(userId);
  },

  setGameState: (gs) => set({ gameState: gs }),
}));
